<template>
  <el-dialog
    v-model="visible"
    title="Please upload:"
    :class="{ mobile: !isDesktop }"
    :closable="!loading"
    :show-close="!loading"
    :close-on-click-modal="!loading"
    @close="close"
  >
    <ul>
      <li>1. Company registration certificate</li>
      <li>2. VAT certificate (if available) or TAX Id certificate</li>
      <li>3. Document confirming the authority of signatory</li>
      <li>
        Any other official document supporting your company payment details can
        be submitted for review as well.
      </li>
    </ul>
    <el-form
      ref="ruleForm"
      v-loading="loading"
      :model="form"
      :rules="rules"
      :validate-on-rule-change="false"
    >
      <el-form-item
        :error="errors.files"
        prop="files"
        label="Documents"
        size="small"
      >
        <FileUpload
          tooltip="jpg, jpeg, png, gif, pdf files with a total size less than 10MB."
          allowed-formats=".pdf, .jpg, .jpeg, .png"
          multiple
          @handleChange="(file, ref, list) => handleFileChange(file, ref, list)"
        />
      </el-form-item>
    </el-form>

    <template #footer>
      <el-button type="primary" :disabled="disabled" @click="submit">
        Submit
      </el-button>
    </template>
  </el-dialog>
</template>

<script lang="ts">
import { defineComponent, ref, computed, Ref } from 'vue'
import FileUpload from '@/components/FileUpload.vue'
import { useContactStore } from '@/store/modules/contact'
import { safeSubmitProcessing } from '@/utils/helpers'
import { IContactDocuments } from '@/api/contacts'
import { ElForm, ElMessage } from 'element-plus'
import useValidation from '@/hooks/useValidation'
import { CommonValidationErrorsKey } from '@/api/types'

import {
  useFileUpload,
  UploadTypes,
  MULTIPLE_UPLOAD_TOOLTIP
} from '@/hooks/useFileUpload'

import useResize from '@/hooks/useResize'

export default defineComponent({
  name: 'DocumentsPopup',
  components: {
    FileUpload
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false
    }
  },
  setup(props, context) {
    const contactStore = useContactStore()
    const files = ref<any>([])
    const loading = ref(false)
    const { isDesktop } = useResize()
    const ruleForm: Ref<ElForm | null> = ref(null)
    const form = ref<any>({
      files: []
    })

    const rules: any = {
      files: [
        {
          required: true,
          message: 'Please upload at least one file',
          trigger: 'change'
        }
      ]
    }

    const { provideValidation, setValidationErrors } = useValidation(
      CommonValidationErrorsKey
    )
    const errors = provideValidation()

    const visible = computed(() => {
      return props.modelValue
    })

    const disabled = computed(() => {
      return form.value.files.length < 1
    })

    const close = () => {
      form.value.files = []
      context.emit('update:modelValue', false)
    }

    const submit = async () => {
      if (loading.value) {
        return
      }

      ruleForm.value?.validate(async (valid: boolean) => {
        if (valid) {
          const payload = { ...form.value } as IContactDocuments
          errors.value = {}
          await safeSubmitProcessing(
            loading,
            () => contactStore.UploadDocuments(payload),
            (response) => {
              close()
              ElMessage.success('Documents has been successfully submited.')
              contactStore.FetchContact()
            },
            (err) => setValidationErrors(err, errors)
          )
        }
      })
    }

    const handleFileChange = (file: any, ref: any, list: any[]) => {
      const isValid = useFileUpload(file, ref, UploadTypes.Document, errors)
      if (isValid) {
        errors.value = {}

        form.value.files = list.map((file: any) => file.raw)
      }
    }
    return {
      handleFileChange,
      isDesktop,
      visible,
      loading,
      close,
      MULTIPLE_UPLOAD_TOOLTIP,
      disabled,
      submit,
      form,
      ruleForm,
      rules,
      errors
    }
  }
})
</script>

<style lang="scss" scoped>
:deep(ul) {
  list-style: none;
  :deep(li) {
    margin: 10px 0;
  }
}
</style>
